import React, { ChangeEvent, forwardRef } from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { TextField, TextFieldProps } from '@ankr.com/ui';
import BigNumber from 'bignumber.js';

import { useValidateNumber } from '../hooks/useValidateNumber';

type Props<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  balance?: BigNumber;
  min?: BigNumber;
  max?: BigNumber;
  unit?: string;
  integerOnly?: boolean;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  required?: boolean;
} & TextFieldProps;

const regExp = /^(\d*\.{0,1}\d{0,18}$)/;
const integerOnlyRegExp = /^(\d*\d{0,18}$)/;

function InputNumberComponent<T extends FieldValues>(
  {
    name,
    control,
    balance,
    onChange,
    min,
    max,
    unit,
    integerOnly,
    placeholder,
    required,
    ...props
  }: Props<T>,
  ref: React.ForwardedRef<HTMLInputElement>,
): JSX.Element {
  const validateAmount = useValidateNumber({
    balance,
    max: balance ?? max,
    min,
    unit,
    integerOnly,
    required,
  });
  const { field } = useController({
    name,
    control,
    rules: { required, validate: validateAmount },
  });

  const handleChange = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ): void => {
    const { value } = event.target;

    const isValid = Boolean(
      integerOnly ? integerOnlyRegExp.exec(value) : regExp.exec(value),
    );
    if (!isValid) {
      return;
    }

    if (onChange) {
      onChange(event);
    }

    field.onChange(event);
  };

  return (
    <TextField
      {...props}
      ref={ref}
      value={field.value ?? ''}
      onChange={handleChange}
      autoComplete="off"
      inputMode="numeric"
      placeholder={placeholder ?? '0'}
    />
  );
}

export const NumberField = forwardRef(InputNumberComponent);
